import { useEffect, useRef, useState, type FormEvent } from 'react';

import { api } from './api';
import { CloseIcon, DebtIcon } from './icons';

export interface OutboundDebtInvoice {
  id: string;
  sku: string;
  partnerName: string;
  remaining: number;
  refund: number;
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

function numeric(value: string): number {
  return Number(value.replace(/[^0-9]/g, '')) || 0;
}

export function OutboundPaymentModal({
  invoice,
  onClose,
  onPaid
}: {
  invoice: OutboundDebtInvoice;
  onClose: () => void;
  onPaid: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [paid, setPaid] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isRefund = Number(invoice.refund) > 0;
  const due = isRefund ? Number(invoice.refund) : Number(invoice.remaining);

  useEffect(() => input.current?.focus(), []);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, saving]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (paid <= 0) {
      setError(`Số tiền ${isRefund ? 'hoàn' : 'thu'} phải lớn hơn 0.`);
      input.current?.focus();
      return;
    }
    if (paid > due) {
      setError(`Số tiền ${isRefund ? 'hoàn' : 'thu'} không được lớn hơn số tiền còn lại.`);
      input.current?.focus();
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api<{ payment: { message: string } }>(`/api/invoices/outbound/${invoice.id}/payments`, {
        method: 'POST',
        body: JSON.stringify({ paid })
      });
      onPaid(result.payment.message);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `Không thể ghi nhận ${isRefund ? 'hoàn tiền' : 'thu nợ'}.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-backdrop payment-backdrop" role="presentation">
      <section className="payment-modal" role="dialog" aria-modal="true" aria-labelledby="outbound-payment-title">
        <header className="drawer-header"><h2 id="outbound-payment-title">{isRefund ? 'Hoàn tiền khách hàng' : 'Thu nợ hóa đơn xuất'}</h2><button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><CloseIcon /></button></header>
        <form onSubmit={submit}>
          <div className="payment-content">
            <div className="payment-invoice">
              <div><span>Mã hóa đơn</span><strong>{invoice.sku}</strong></div>
              <div><span>Khách hàng</span><strong title={invoice.partnerName}>{invoice.partnerName}</strong></div>
              <div className="payment-remaining"><span>{isRefund ? 'Cần hoàn' : 'Còn phải thu'}</span><b>{money(due)} ₫</b></div>
            </div>
            <div className="field payment-field">
              <label htmlFor="outbound-paid">Số tiền {isRefund ? 'hoàn' : 'thu'}</label>
              <div className="payment-field-control">
                <input ref={input} id="outbound-paid" inputMode="numeric" value={money(paid)} aria-invalid={Boolean(error)} onChange={(event) => { setPaid(numeric(event.target.value)); setError(''); }} />
                <button type="button" title="Điền toàn bộ số tiền" onClick={() => { setPaid(due); setError(''); }}>{isRefund ? 'Hoàn toàn bộ' : 'Thu toàn bộ'}</button>
              </div>
              {error ? <span className="field-error" role="alert">{error}</span> : null}
            </div>
          </div>
          <footer className="drawer-footer"><button type="button" className="secondary-action" onClick={onClose}>Hủy</button><button className="primary-action" disabled={saving}>{saving ? <span className="button-spinner" /> : <DebtIcon />} {saving ? 'Đang lưu…' : `Xác nhận ${isRefund ? 'hoàn tiền' : 'thu nợ'}`}</button></footer>
        </form>
      </section>
    </div>
  );
}
